import React, { useState } from "react";
import StudentEnrollment from "./StudentEnrollment";
import VolunteerRegistration from "./VolunteerRegistration";
import VolunteerRecommendation from "./VolunteerRecommendation";
import {
  Container,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Box,
  Paper,
  ThemeProvider,
  createTheme
} from '@mui/material';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import SchoolIcon from '@mui/icons-material/School';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import ChatIcon from '@mui/icons-material/Chat';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Header from "./Header";
import { useLanguage } from "../context/LanguageContext";
import LanguageModal from './LanguageModal';

const theme = createTheme({
  palette: {
    primary: {
      main: '#E27A58',
    },
    secondary: {
      main: '#2D3849',
    },
    background: {
      default: '#F5F5F5',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
    },
    h2: {
      fontWeight: 600,
    },
    h3: {
      fontWeight: 600,
    },
  },
});

const Homepage = ({ user, onEditProfile, preferredLanguage }) => {
  const { t, currentLanguage, getLanguageName, isRtl } = useLanguage();
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [enrollmentType, setEnrollmentType] = useState(null);
  const [volunteerType, setVolunteerType] = useState(null);

  if (enrollmentType) {
    return (
      <ThemeProvider theme={theme}>
        <StudentEnrollment
          enrollmentType={enrollmentType}
          userEmail={user?.user_email}
          onBack={() => setEnrollmentType(null)}
          t={t}
          onProfileClick={onEditProfile}
        />
      </ThemeProvider>
    );
  }

  if (volunteerType && volunteerType === 'Volunteer Registration') {
    return (
      <ThemeProvider theme={theme}>
        <VolunteerRegistration
          volunteerType={volunteerType}
          userEmail={user?.user_email}
          onBack={() => setVolunteerType(null)}
          t={t}
          onProfileClick={onEditProfile}
        />
      </ThemeProvider>
    );
  }

  if (volunteerType && volunteerType === 'Volunteer Recommendation') {
    return (
      <ThemeProvider theme={theme}>
        <VolunteerRecommendation
          volunteerType={volunteerType}
          userEmail={user?.user_email}
          onBack={() => setVolunteerType(null)}
          t={t}
          onProfileClick={onEditProfile}
        />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          bgcolor: "background.default",
          direction: isRtl ? 'rtl' : 'ltr',
        }}
      >
        {/* Fixed Header */}
        <Header
          showProfileIcon
          onProfileClick={onEditProfile}
          onLanguageClick={() => setShowLanguageModal(true)}
          languageLabel={getLanguageName(currentLanguage)}
        />
        <LanguageModal
          open={showLanguageModal}
          onClose={() => setShowLanguageModal(false)}
        />

        {/* Scrollable Main Content */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            px: { xs: 2, sm: 0 },
          }}
        >
          <Container component="main" sx={{ mt: 4, mb: 4 }}>
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h4"
                component="h1"
                color="secondary.main"
                gutterBottom
              >
                {t("welcomeForm")}
                {user?.first_name ? `, ${user.first_name}!` : "!"}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {t("selectFormPrompt")}
              </Typography>
            </Box>

            <Grid container spacing={3}>
              {/* Reading Circle Card */}
              <Grid item xs={12} sm={6} md={4}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderTop: 4,
                    borderColor: "primary.main",
                    transition: "box-shadow 0.3s",
                    "&:hover": {
                      boxShadow: 6,
                      cursor: "pointer",
                    },
                  }}
                  onClick={() => setEnrollmentType("Reading Circle")}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                      <Box
                        sx={{
                          bgcolor: "primary.light",
                          p: 1.5,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <MenuBookIcon sx={{ color: 'white' }} fontSize="large" />
                      </Box>
                    </Box>
                    <Typography
                      variant="h6"
                      component="h2"
                      gutterBottom
                      align="center"
                      color="secondary.main"
                    >
                      {t("readingCircleEnrollment")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t("readingCircleDescription")}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ justifyContent: "flex-end", pb: 2, px: 2 }}>
                  <Button
                    color="primary"
                    endIcon={
                      <ArrowForwardIcon sx={{
                        transform: isRtl ? 'scaleX(-1)' : 'none'
                      }} />
                    }
                  >
                    {t("beginForm")}
                  </Button>
                  </CardActions>
                </Card>
              </Grid>

              {/* Preschool Circle Card */}
              <Grid item xs={12} sm={6} md={4}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderTop: 4,
                    borderColor: "primary.main",
                    transition: "box-shadow 0.3s",
                    "&:hover": {
                      boxShadow: 6,
                      cursor: "pointer",
                    },
                  }}
                  onClick={() => setEnrollmentType("Preschool Circle")}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                      <Box
                        sx={{
                          bgcolor: "primary.light",
                          p: 1.5,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <SchoolIcon sx={{ color: 'white' }} fontSize="large" />
                      </Box>
                    </Box>
                    <Typography
                      variant="h6"
                      component="h2"
                      gutterBottom
                      align="center"
                      color="secondary.main"
                    >
                      {t("preschoolCircleEnrollment")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t("preschoolCircleDescription")}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ justifyContent: "flex-end", pb: 2, px: 2 }}>
                  <Button
                    color="primary"
                    endIcon={
                      <ArrowForwardIcon sx={{
                        transform: isRtl ? 'scaleX(-1)' : 'none'
                      }} />
                    }
                  >
                    {t("beginForm")}
                  </Button>
                  </CardActions>
                </Card>
              </Grid>

              {/* Volunteer Registration */}
              <Grid item xs={12} sm={6} md={4}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderTop: 4,
                    borderColor: "primary.main",
                    transition: "box-shadow 0.3s",
                    "&:hover": {
                      boxShadow: 6,
                      cursor: "pointer",
                    },
                  }}
                  onClick={() => setVolunteerType("Volunteer Registration")}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                      <Box
                        sx={{
                          bgcolor: "primary.light",
                          p: 1.5,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <VolunteerActivismIcon sx={{ color: 'white' }} fontSize="large" />
                      </Box>
                    </Box>
                    <Typography
                      variant="h6"
                      component="h2"
                      gutterBottom
                      align="center"
                      color="secondary.main"
                    >
                      Volunteer Registration
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Are you ready to become a Reading Circle or Preschool Circle Volunteer? We're so excited to walk alongside you on this journey!
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ justifyContent: "flex-end", pb: 2, px: 2 }}>
                  <Button
                    color="primary"
                    endIcon={
                      <ArrowForwardIcon sx={{
                        transform: isRtl ? 'scaleX(-1)' : 'none'
                      }} />
                    }
                  >
                    Begin Form
                  </Button>
                  </CardActions>
                </Card>
              </Grid>

              {/* Volunteer Recommendation */}
              <Grid item xs={12} sm={6} md={4}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderTop: 4,
                    borderColor: "primary.main",
                    transition: "box-shadow 0.3s",
                    "&:hover": {
                      boxShadow: 6,
                      cursor: "pointer",
                    },
                  }}
                  onClick={() => setVolunteerType("Volunteer Recommendation")}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                      <Box
                        sx={{
                          bgcolor: "primary.light",
                          p: 1.5,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <ChatIcon sx={{ color: 'white' }} fontSize="large" />
                      </Box>
                    </Box>
                    <Typography
                      variant="h6"
                      component="h2"
                      gutterBottom
                      align="center"
                      color="secondary.main"
                    >
                      Volunteer Recommendation
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Are you here to complete a Volunteer Recommendation Request?
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ justifyContent: "flex-end", pb: 2, px: 2 }}>
                  <Button
                    color="primary"
                    endIcon={
                      <ArrowForwardIcon sx={{
                        transform: isRtl ? 'scaleX(-1)' : 'none'
                      }} />
                    }
                  >
                    Begin Form
                  </Button>
                  </CardActions>
                </Card>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Fixed Footer */}
        <Box
          sx={{
            p: 2,
            bgcolor: "#1EAEB8",
            color: "#fff",
            textAlign: "center",
          }}
        >
          <Typography variant="body2">{t("footerText")}</Typography>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default Homepage;
